import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Hook de detección de inactividad del usuario.
 * Detecta eventos de mouse, teclado, scroll y touch para resetear el timer.
 * Retorna estados que el componente puede usar para mostrar UI de aviso/cierre.
 *
 * @param {object} opts
 * @param {number} opts.idleTimeoutMs - tiempo total sin actividad antes de cerrar (ms)
 * @param {number} opts.warningMs - tiempo antes del cierre cuando se muestra warning (ms)
 * @param {function} opts.onTimeout - callback cuando se cumple el timeout sin extensión
 * @param {boolean} opts.enabled - habilita o pausa el monitor (ej. desactivar en /login)
 */
export default function useIdleTimeout({ idleTimeoutMs, warningMs = 60_000, onTimeout, enabled = true }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.floor(warningMs / 1000));

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const expireTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (expireTimerRef.current) clearTimeout(expireTimerRef.current);
    if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
  }, []);

  const reset = useCallback(() => {
    if (!enabled) return;
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setSecondsRemaining(Math.floor(warningMs / 1000));
    clearAllTimers();

    // Programar el aviso de warning
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsRemaining(Math.floor(warningMs / 1000));
      // tick visual del countdown
      tickIntervalRef.current = setInterval(() => {
        setSecondsRemaining((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, idleTimeoutMs - warningMs);

    // Programar el cierre forzado
    expireTimerRef.current = setTimeout(() => {
      clearAllTimers();
      setShowWarning(false);
      if (onTimeout) onTimeout();
    }, idleTimeoutMs);
  }, [idleTimeoutMs, warningMs, onTimeout, enabled, clearAllTimers]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }
    const handleActivity = () => {
      // Si ya está mostrando warning, no resetear automáticamente — el usuario debe decidir
      if (showWarning) return;
      reset();
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));
    reset();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handleActivity));
      clearAllTimers();
    };
  }, [enabled, idleTimeoutMs, warningMs, reset, showWarning, clearAllTimers]);

  return {
    showWarning,
    secondsRemaining,
    extendSession: reset,
    cancelWarning: () => {
      setShowWarning(false);
      clearAllTimers();
    },
  };
}
